/**
 * Interim fan-out adapters (CT-5091).
 *
 * No unified Work Queue events endpoint exists yet (owned by CT-4867). Until it
 * ships, the inbox list is assembled by fanning out over the governance "My Work"
 * lists and normalizing each source row into the raw event shape that
 * {@link mapApiEventToUi} consumes.
 *
 * Field handling mirrors the PROVEN beta Governance Studio "My Work" hooks
 * (useMyWork{ChangeRequests,Proposals,SuggestionClaims,Tasks}) — the reference
 * for the real GS field names, which differ from the ticket's documented shapes
 * (e.g. `proposalUuid` is frequently null on the wire, so the id falls back to
 * `id`/`truthUuid`; risk/timestamps arrive camelCase OR snake_case).
 *
 * Deliberate type choice: these sources (change requests, proposals, claim
 * suggestions, tasks) are NOT the nine HTL event types from the prototype, so we
 * tag them with INTERIM type strings that are intentionally absent from
 * `EVENT_TYPE`/`EVENT_BODY_REGISTRY` → the detail panel falls back to the generic
 * body + generic decision footer (crash-safe). Real per-type detail: CT-5092.
 */

import { CATEGORY } from '../constants/eventTypes.js';
import { SEVERITY } from '../constants/severities.js';
import { DUE_BUCKET } from '../constants/filterOptions.js';
import { INTERIM_EVENT_TYPE } from '../constants/interimEventTypes.js';
import { buildTaskOwner } from './taskOwner.js';
import { humanizeReason } from './textCase.js';
import { pendingSuggestionEntry, pendingSuggestionText } from './mapClaimToTrainMeDetail.js';

/**
 * Maps a governance risk level (`Low|Medium|High|Critical`, any casing) onto the
 * Work Queue severity tiers. Unknown/absent risk defaults to `action` (neutral).
 */
export function riskToSeverity(riskLevel) {
  switch (String(riskLevel ?? '').toLowerCase()) {
    case 'critical':
      return SEVERITY.CRITICAL;
    case 'high':
      return SEVERITY.ACT_NOW;
    case 'medium':
      return SEVERITY.ACTION;
    case 'low':
      return SEVERITY.HEADS_UP;
    default:
      return SEVERITY.ACTION;
  }
}

/** First 8 chars of a uuid, uppercased — a compact human-facing reference. */
function shortRef(uuid) {
  return uuid ? String(uuid).slice(0, 8).toUpperCase() : null;
}

/**
 * Maps a raw backend status onto the prototype's inbox status vocabulary. The
 * prototype (source of truth) has no `pending_approval` chip at event level —
 * awaiting-action events use `open` (hidden default; see EVT-012). Aliases
 * collapse the GS approval-pending family onto it; anything else passes through
 * lowercased and is translated/humanized downstream.
 */
const STATUS_ALIASES = {
  pending_approval: 'open',
  awaiting_approval: 'open',
  in_review: 'open',
};
export function toWorkQueueStatus(raw) {
  const s = String(raw ?? 'open').toLowerCase();
  return STATUS_ALIASES[s] ?? s;
}

/**
 * A claim row belongs in the inbox only when it has a pending suggestion awaiting
 * review (mirrors the beta's `hasPendingSuggestion` gate).
 */
export function claimHasPendingSuggestion(c = {}) {
  if (c.hasPendingSuggestion === true) return true;
  if (Array.isArray(c.suggestions) && c.suggestions.some((s) => (s?.status ?? 'pending') === 'pending')) return true;
  return false;
}

// These interim sources carry no due date, so the inbox shows them as "no date".
const NO_DUE = { dueLabel: null, dueBucket: DUE_BUCKET.NO_DATE, dueAt: null, overdue: false };

/**
 * Change request (per KB) → raw event. Proposed values often live inside
 * `proposedFactPatch`; top-level keys are frequently null on the wire, so we read
 * the patch first and fall back to the flat fields.
 */
export function mapChangeRequestToEvent(cr = {}, kb = {}, scope, index = 0) {
  const patch = cr.proposedFactPatch ?? {};
  const title = patch.factTitle ?? patch.title ?? cr.factTitle ?? 'Change request';
  const proposed = patch.statement ?? cr.proposedValueText ?? cr.suggestedText ?? '';
  const id = cr.changeRequestUuid ?? cr.id ?? `${kb.knowledgebaseUuid ?? 'kb'}-cr-${index}`;
  return {
    id,
    scope,
    type: INTERIM_EVENT_TYPE.CHANGE_REQUEST,
    category: CATEGORY.GOV,
    severity: riskToSeverity(patch.riskLevel ?? cr.riskLevel ?? cr.risk_level),
    title,
    description: proposed || cr.reason || '',
    refId: shortRef(id),
    refLabel: kb.name ?? 'Change Request',
    actionLabel: 'Review',
    studio: kb.departmentName ?? kb.domain ?? 'governance',
    status: toWorkQueueStatus(cr.changeStatus),
    ...NO_DUE,
    isCustomer: false,
    missionCritical: (patch.urgency ?? cr.urgency) === 'Urgent',
    commentsCount: 0,
    situation: proposed || cr.reason || '',
    pipeline: [],
    detail: {
      source: INTERIM_EVENT_TYPE.CHANGE_REQUEST,
      changeRequestUuid: cr.changeRequestUuid ?? null,
      knowledgebaseUuid: kb.knowledgebaseUuid ?? null,
      knowledgebaseName: kb.name ?? null,
      truthUuid: cr.truthUuid ?? cr.factUuid ?? null,
      confidence: patch.confidence ?? cr.confidence ?? null,
      riskLevel: patch.riskLevel ?? cr.riskLevel ?? cr.risk_level ?? null,
      proposedStatement: proposed || null,
      rationale: patch.rationale ?? cr.rationale ?? null,
      // Adaptable fields mapped onto the Gov Review body's UI slots
      // (reason→requestReason, hasConflict→conflict); approvals feed the review
      // progress. The preview endpoint is 500-broken, so these come from the list row.
      requestReason: humanizeReason(cr.reason) ?? patch.rationale ?? null,
      conflict: cr.hasConflict ?? false,
      approvalCount: cr.approvalCount ?? null,
      requiredApprovals: cr.requiredApprovals ?? null,
      assignedTo: cr.assignedTo ?? null,
      updatedAt: cr.updatedAt ?? cr.updated_at ?? null,
      raw: cr,
    },
  };
}

/**
 * The pending suggestion's uuid for a claim (CT-5095). The list exposes every
 * suggestion under `metadata.suggestions[]` (snake_case), each with a `status`;
 * accept/decline act on the one still `pending`. Mirrors the beta's
 * `suggestions.find(s => s.status === 'pending')`. Returns null when none pending.
 */
export function pendingSuggestionUuid(c = {}) {
  const pending = pendingSuggestionEntry(c);
  return pending?.suggestion_uuid ?? pending?.suggestionUuid ?? null;
}

/** Claim suggestion (per sandbox) → raw event. */
export function mapClaimToEvent(c = {}, sandbox = {}, scope, index = 0) {
  const id = c.claimUuid ?? c.id ?? `${sandbox.sandboxUuid ?? 'sb'}-claim-${index}`;
  return {
    id,
    scope,
    type: INTERIM_EVENT_TYPE.CLAIM_SUGGESTION,
    category: CATEGORY.AGNT,
    severity: riskToSeverity(c.riskLevel ?? c.risk_level),
    title: c.title ?? 'Claim suggestion',
    description: c.description ?? c.claimText ?? c.claim_text ?? '',
    refId: shortRef(id),
    refLabel: sandbox.name ?? 'Sandbox',
    actionLabel: 'Review',
    studio: sandbox.scope ?? sandbox.name ?? 'sandbox',
    status: toWorkQueueStatus(c.status),
    ...NO_DUE,
    isCustomer: false,
    missionCritical: false,
    commentsCount: 0,
    situation: c.description ?? c.claimText ?? c.claim_text ?? '',
    pipeline: [],
    detail: {
      source: INTERIM_EVENT_TYPE.CLAIM_SUGGESTION,
      claimUuid: c.claimUuid ?? null,
      sandboxUuid: sandbox.sandboxUuid ?? null,
      sandboxName: sandbox.name ?? null,
      confidence: c.confidenceLevel ?? c.confidence ?? null,
      riskLevel: c.riskLevel ?? c.risk_level ?? null,
      claimText: c.claimText ?? c.claim_text ?? null,
      hasPendingSuggestion: c.hasPendingSuggestion ?? c.metadata?.has_pending_suggestion ?? false,
      // The pending suggestion's uuid — required by accept/decline (CT-5095). Lives in
      // metadata.suggestions[] on the list row (the preview does NOT carry it).
      suggestionUuid: pendingSuggestionUuid(c),
      // Proposed text for the EVT-011 current → proposed diff (when the list carries it).
      suggestedText: pendingSuggestionText(c),
      updatedAt: c.updatedAt ?? c.updated_at ?? null,
      raw: c,
    },
  };
}

/**
 * Task (cross-plane) → raw event. Tasks carry an explicit assignee, so they are
 * the one interim source with an owner concept — surfaced so My Team can show who
 * holds the task. The list payload has no entity details; `entityType`/`entityUuid`
 * point at the underlying record (e.g. a change_request), not the task's own type.
 */
export function mapTaskToEvent(task = {}, scope, index = 0, ownerContext = {}) {
  const id = task.taskUuid ?? task.id ?? `task-${index}`;
  const severity = riskToSeverity(task.severity);
  const entityLabel = humanizeReason(task.entityType ?? 'task');
  const reasonLabel = humanizeReason(task.reason);
  const title =
    reasonLabel ||
    humanizeReason(task.message) ||
    `${entityLabel} ${shortRef(task.entityUuid) ?? ''}`.trim();
  return {
    id,
    scope,
    type: INTERIM_EVENT_TYPE.TASK,
    category: CATEGORY.AGNT,
    severity,
    title,
    description: task.message ?? '',
    refId: shortRef(id),
    // Breadcrumb/meta: the assignment reason, not the linked entity kind (that
    // lives in detail.entityType and is labeled separately in the task UI).
    refLabel: reasonLabel ?? entityLabel,
    actionLabel: 'Resolve',
    studio: 'tasks',
    status: toWorkQueueStatus(task.status),
    ...NO_DUE,
    isCustomer: false,
    missionCritical: severity === SEVERITY.CRITICAL || severity === SEVERITY.ACT_NOW,
    commentsCount: 0,
    situation: task.message ?? '',
    pipeline: [],
    owner: buildTaskOwner(task.assigneeUuid, ownerContext),
    detail: {
      source: INTERIM_EVENT_TYPE.TASK,
      taskUuid: task.taskUuid ?? null,
      entityType: task.entityType ?? null,
      entityUuid: task.entityUuid ?? null,
      assigneeUuid: task.assigneeUuid ?? null,
      createdByUuid: task.createdByUuid ?? null,
      reasonLabel,
      severityLabel: task.severity ?? null,
      updatedAt: task.updatedAt ?? null,
      ...(task.__seed ? { __seed: true } : {}),
      raw: task,
    },
  };
}
